import { Coordinate2D, Path, PopupDetails } from '@/types'
import Konva from 'konva'

export namespace Shapes {
  export enum Capability {
    Selectable,
    Highlightable,
    Fadable,
    Focusable,
    Popup,
    Draggable,
  }

  interface EntityBase {
    key: string
    path: Path
    center: Coordinate2D
    capabilities: Capability[]
    refresh: () => void
    onAdded?: () => void
  }

  export interface Shape extends Konva.Shape, EntityBase {}

  export interface Group extends Konva.Group, EntityBase {
    // eslint-disable-next-line no-use-before-define
    entities: Entity[]
  }

  export type Entity = Shape | Group

  export interface Selectable {
    isSelected: boolean
  }
  export type SelectableEntity = Entity & Selectable

  export interface Highlightable {
    isHighlighted: boolean
  }
  export type HighlightableEntity = Entity & Highlightable

  export interface HasPopup {
    popupDetails: PopupDetails
    left: number
    top: number
  }
  export type HasPopupEntity = Entity & HasPopup

  export interface Draggable {
    dragstart?: (event: Konva.KonvaEventObject<DragEvent>) => void
    dragmove?: (event: Konva.KonvaEventObject<DragEvent>) => void
    dragend?: (event: Konva.KonvaEventObject<DragEvent>) => void
  }
  export type DraggableEntity = Entity & Draggable

  export function isShape(obj: any): obj is Shape {
    return obj instanceof Konva.Shape && 'path' in obj && 'capabilities' in obj
  }

  export function isGroup(obj: any): obj is Group {
    return obj instanceof Konva.Group && 'path' in obj && 'capabilities' in obj
  }

  export function isEntity(obj: any): obj is Entity {
    return isShape(obj) || isGroup(obj)
  }

  export function isSelectable(obj: any): obj is SelectableEntity {
    return isEntity(obj) && obj.capabilities.includes(Capability.Selectable) && 'isSelected' in obj
  }

  export function isHighlightable(obj: any): obj is HighlightableEntity {
    return (
      isEntity(obj) && obj.capabilities.includes(Capability.Highlightable) && 'isHighlighted' in obj
    )
  }

  export function isFadable(obj: any): boolean {
    return isEntity(obj) && obj.capabilities.includes(Capability.Fadable)
  }

  export function isFocusable(obj: any): boolean {
    return isEntity(obj) && obj.capabilities.includes(Capability.Focusable)
  }

  export function hasPopup(obj: any): obj is HasPopupEntity {
    return isEntity(obj) && obj.capabilities.includes(Capability.Popup) && 'popupDetails' in obj
  }

  export function isDraggable(obj: any): obj is DraggableEntity {
    return (
      isEntity(obj) &&
      obj.capabilities.includes(Capability.Draggable) &&
      ('dragstart' in obj || 'dragmove' in obj || 'dragend' in obj)
    )
  }
}
